import { useMemo } from 'react';
import {
  Button,
  Body2,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { Dismiss24Regular } from '@fluentui/react-icons';

import { diffWords } from '../../utils';
import { TFindingChange } from '../../types';

const useStyles = makeStyles({
  surface: {
    width: 'min(900px, calc(100vw - 48px))',
    maxWidth: '900px',
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
  viewport: {
    padding: tokens.spacingHorizontalL,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground1,
    lineHeight: tokens.lineHeightBase400,
    maxHeight: 'min(520px, calc(100vh - 320px))',
    overflowY: 'auto',
  },
  removed: {
    paddingLeft: '2px',
    paddingRight: '2px',
    borderRadius: tokens.borderRadiusSmall,
    color: tokens.colorNeutralForeground1,
    backgroundColor: tokens.colorPaletteRedBackground1,
    textDecorationLine: 'line-through',
    textDecorationColor: tokens.colorPaletteRedForeground1,
    borderBottom: `2px solid ${tokens.colorPaletteRedBorder1}`,
  },
  added: {
    paddingLeft: '2px',
    paddingRight: '2px',
    borderRadius: tokens.borderRadiusSmall,
    color: tokens.colorPaletteGreenForeground1,
    backgroundColor: tokens.colorPaletteGreenBackground1,
    borderBottom: `2px solid ${tokens.colorPaletteGreenBorder1}`,
  },
});

type TProps = {
  change?: TFindingChange;
  explanation?: string;
  isAccepted?: boolean;
  onAccept: () => void;
  onClose: () => void;
};

// Change mode shows the proposed text as a tracked change: removed wording struck
// through in red, new wording marked in green.
export const ChangeModeDialog = ({ change, explanation, isAccepted, onAccept, onClose }: TProps) => {
  const classes = useStyles();
  const segments = useMemo(
    () => (change ? diffWords(change.currentText, change.proposedText) : []),
    [change?.currentText, change?.proposedText]
  );

  return (
    <Dialog open={Boolean(change)} onOpenChange={(_, data) => !data.open && onClose()}>
      <DialogSurface className={classes.surface}>
        <DialogBody>
          <DialogTitle
            action={<Button appearance="subtle" aria-label="close" icon={<Dismiss24Regular />} onClick={onClose} />}
          >
            {change?.document ?? 'Proposed change'}
          </DialogTitle>

          <DialogContent className={classes.content}>
            {explanation ? <Body2>{explanation}</Body2> : null}

            <div className={classes.viewport}>
              <Body2 className="whitespace-pre-line">
                {segments.map((segment, index) => {
                  if (segment.type === 'equal') return <span key={index}>{segment.text}</span>;
                  return (
                    <span key={index} className={segment.type === 'removed' ? classes.removed : classes.added}>
                      {segment.text}
                    </span>
                  );
                })}
              </Body2>
            </div>
          </DialogContent>

          <DialogActions>
            <Button appearance="secondary" onClick={onClose}>
              {isAccepted ? 'Close' : 'Reject'}
            </Button>
            {!isAccepted ? (
              <Button appearance="primary" onClick={onAccept}>
                Accept
              </Button>
            ) : null}
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};
